/**
 * Marina AI - Staff Operations Assistant
 * Chat interface for front desk staff
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { MessageCircle, Send, Sparkles, X, Minimize2, Maximize2, Loader2, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { BACKEND_URL, API } from '@/lib/backendUrl';


export const MarinaChat = ({ 
  hotelId, 
  onCommand, 
  currentUser,
  isOpen = false,
  onToggle,
  department = "front_desk" // front_desk, housekeeping, maintenance
}) => {
  
  // Get language from context
  const { language } = useLanguage();
  
  // Department-specific welcome messages
  const getWelcomeMessage = () => {
    const welcomeMessages = {
      front_desk: `👋 Hi ${currentUser?.name || 'there'}! I'm Marina, your voice-enabled AI assistant.\n\nI can help you with:\n• Check in/out guests\n• Search rooms & guests\n• View arrivals/departures\n• Close your shift\n\n🎤 Click the microphone to speak or just type!`,
      
      housekeeping: `🧹 Hi ${currentUser?.name || 'there'}! I'm Marina, your housekeeping assistant.\n\nI can help you with:\n• Mark rooms clean/dirty/inspected\n• Show your tasks\n• Report issues & lost items\n• Request supplies\n\n🎤 Click the microphone to speak or just type!`,
      
      maintenance: `🔧 Hi ${currentUser?.name || 'there'}! I'm Marina, your maintenance assistant.\n\nI can help you with:\n• Create work orders\n• View & update tickets\n• Check equipment status\n• Request parts\n\n🎤 Click the microphone to speak or just type!`
    };
    
    return welcomeMessages[department] || welcomeMessages.front_desk;
  };
  
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true); // Auto-speak responses
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Initialize Web Speech API
  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setVoiceSupported(true);
      
      const recognition = new SpeechRecognition();
      recognition.continuous = false; // Stop after one result
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
        console.log('🎤 Voice recognition started');
      };
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('🎤 Heard:', transcript);
        setInput(transcript);
        setIsListening(false);
        
        // Auto-send after voice input
        setTimeout(() => {
          if (transcript.trim()) {
            handleSend(transcript);
          }
        }, 500);
      };
      
      recognition.onerror = (event) => {
        console.error('🎤 Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable microphone permissions.');
        } else if (event.error === 'no-speech') {
          toast.info('No speech detected. Please try again.');
        } else if (event.error !== 'aborted') {
          toast.error('Voice recognition error. Please try typing instead.');
        }
      };
      
      recognition.onend = () => {
        setIsListening(false);
        console.log('🎤 Voice recognition ended');
      };
      
      recognitionRef.current = recognition;
    } else {
      console.warn('⚠️  Speech recognition not supported in this browser');
      setVoiceSupported(false);
    }
    
    // Initialize Text-to-Speech
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      console.log('🔊 Text-to-speech initialized');
    }
    
    // Cleanup
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const handleSend = async (voiceTranscript = null) => {
    const messageText = voiceTranscript || input.trim();
    if (!messageText || loading) return;

    // Clear input if not from voice
    if (!voiceTranscript) {
      setInput('');
    }

    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      // Call Marina AI API
      const response = await fetch(`${BACKEND_URL}/api/marina/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId || 'unknown',
          user_id: currentUser?.id || currentUser?.email || 'unknown',
          message: messageText,
          conversation_history: messages.slice(-5), // Last 5 messages for context
          department: department, // front_desk, housekeeping, or maintenance
          language: language || 'en' // Pass selected language to backend
        })
      });

      const data = await response.json();

      if (data.success) {
        // Add Marina's response
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          action: data.action // e.g., "open_checkin_dialog"
        };
        
        setMessages(prev => [...prev, assistantMessage]);

        // Speak the response if voice is enabled
        if (voiceEnabled && synthRef.current) {
          speakText(data.response);
        }

        // Execute action if provided
        if (data.action && onCommand) {
          onCommand(data.action, data.data);
        }
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Marina error:', error);
      const errorMessage = {
        role: 'assistant',
        content: '⚠️ Sorry, I encountered an error. Please try again or contact support if this persists.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to communicate with Marina');
    } finally {
      setLoading(false);
      setInput(''); // Clear input after voice or typed message
    }
  };

  const speakText = (text) => {
    if (!synthRef.current) return;
    
    // Cancel any ongoing speech
    synthRef.current.cancel();
    
    // Clean text for speech (remove emojis and special formatting)
    const cleanText = text
      .replace(/[🎤🔊✅📋🔍🏨💰📊⏰📅🚶⚠️👋😊]/g, '') // Remove emojis
      .replace(/\*\*/g, '') // Remove bold markdown
      .replace(/\n/g, '. ') // Convert line breaks to pauses
      .trim();
    
    if (!cleanText) return;
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.1; // Slightly faster for efficiency
    utterance.pitch = 1.0;
    utterance.volume = 0.9;
    
    // Select a female voice if available
    const voices = synthRef.current.getVoices();
    const femaleVoice = voices.find(voice => 
      voice.name.includes('Female') || 
      voice.name.includes('Samantha') ||
      voice.name.includes('Google UK English Female')
    );
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }
    
    utterance.onstart = () => {
      setIsSpeaking(true);
      console.log('🔊 Marina speaking:', cleanText);
    };
    
    utterance.onend = () => {
      setIsSpeaking(false);
      console.log('🔊 Marina finished speaking');
    };
    
    utterance.onerror = (event) => {
      console.error('🔊 Speech synthesis error:', event);
      setIsSpeaking(false);
    };
    
    synthRef.current.speak(utterance);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    
    if (isListening) {
      // Stop listening
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      // Stop any ongoing speech first
      if (synthRef.current) {
        synthRef.current.cancel();
        setIsSpeaking(false);
      }
      
      // Start listening
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting voice recognition:', error);
        toast.error('Could not start voice input. Please try again.');
      }
    }
  };

  const toggleVoiceOutput = () => {
    setVoiceEnabled(!voiceEnabled);
    if (!voiceEnabled) {
      toast.success('🔊 Voice responses enabled');
    } else {
      toast.info('🔇 Voice responses disabled');
      // Stop any ongoing speech
      if (synthRef.current) {
        synthRef.current.cancel();
        setIsSpeaking(false);
      }
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={onToggle}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 z-50"
        data-testid="marina-chat-button"
      >
        <div className="relative">
          <MessageCircle className="w-7 h-7 text-white" />
          <Sparkles className="w-4 h-4 text-yellow-300 absolute -top-1 -right-1 animate-pulse" />
        </div>
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-6 right-6 shadow-2xl border-2 border-blue-200 z-50 transition-all duration-300 ${
      isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
    }`}>
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Marina AI</h3>
              <p className="text-xs text-blue-100">Staff Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Chat Messages */}
      {!isMinimized && (
        <>
          <CardContent className="p-4 h-[450px] overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                        : msg.isError
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-white text-gray-900 shadow-md border border-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={`text-xs mt-1 ${
                      msg.role === 'user' ? 'text-blue-100' : 'text-gray-400'
                    }`}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-md border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm text-gray-600">Marina is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          {/* Input Area */}
          <div className="p-4 border-t bg-white">
            {/* Voice controls bar - Always show */}
            <div className="flex items-center justify-between mb-2 pb-2 border-b">
              <div className="flex items-center gap-2">
                {isListening && (
                  <span className="text-xs text-blue-600 font-semibold flex items-center gap-1 animate-pulse">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    Listening...
                  </span>
                )}
                {isSpeaking && (
                  <span className="text-xs text-purple-600 font-semibold flex items-center gap-1">
                    <Volume2 className="w-3 h-3" />
                    Speaking...
                  </span>
                )}
                {!isListening && !isSpeaking && (
                  <span className="text-xs text-gray-500">
                    {voiceSupported ? '🎤 Voice ready' : '⚠️ Voice not supported'}
                  </span>
                )}
              </div>
              {voiceSupported && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleVoiceOutput}
                  className="h-6 px-2 text-xs"
                  title={voiceEnabled ? "Mute voice responses" : "Enable voice responses"}
                >
                  {voiceEnabled ? (
                    <><Volume2 className="w-3 h-3 mr-1" /> On</>
                  ) : (
                    <><VolumeX className="w-3 h-3 mr-1" /> Off</>
                  )}
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              {/* Microphone Button - Always show, disable if not supported */}
              <Button
                onClick={startVoiceInput}
                disabled={loading || !voiceSupported}
                className={`h-12 px-4 transition-all duration-300 ${
                  isListening 
                    ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                    : voiceSupported
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                data-testid="marina-voice-button"
                title={!voiceSupported ? "Voice not supported in this browser" : isListening ? "Stop listening" : "Click to speak"}
              >
                {isListening ? (
                  <MicOff className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </Button>
              
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={voiceSupported ? "Type or speak a command..." : "Type a command..."}
                className="flex-1 h-12"
                disabled={loading || isListening}
                data-testid="marina-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading || isListening}
                className="h-12 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                data-testid="marina-send-button"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Quick Commands */}
            <div className="flex gap-2 mt-2 flex-wrap">
              {['Arrivals', 'Departures', 'Occupancy', 'Close Shift'].map((cmd) => (
                <Badge
                  key={cmd}
                  variant="secondary"
                  className="cursor-pointer hover:bg-blue-100 text-xs"
                  onClick={() => setInput(`Show ${cmd.toLowerCase()}`)}
                >
                  {cmd}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
